import { FC, useEffect, useRef, useState, useCallback } from "react"
import {
  View,
  ViewStyle,
  TextStyle,
  TouchableOpacity,
  Dimensions,
  Platform,
  ScrollView,
} from "react-native"
import * as Sentry from "@sentry/react"
import Animated, { FadeIn, FadeOut, SlideInDown } from "react-native-reanimated"
import Icon from "react-native-vector-icons/MaterialIcons"

import { CelebrationBurst } from "@/components/CelebrationBurst"
import { Text } from "@/components/Text"
import { submitReferralSurvey } from "@/services/firebase"
import type { SurveyType } from "@/services/firebase/functions"
import { colors, spacing, typography } from "@/theme"

// ─── Types ─────────────────────────────────────────────────────
type Phase = "idle" | "asking" | "thankyou" | "hidden"

interface ReferralSurveyProps {
  trackingToken: string
  visible: boolean
  onSurveyShow?: () => void
  onSurveyHide?: () => void
}

// ─── Survey Steps Configuration ────────────────────────────────

interface SurveyStep {
  surveyType: SurveyType
  question: string
  options: readonly { label: string; value: string }[]
  introText?: string
}

const SURVEY_STEPS: SurveyStep[] = [
  {
    surveyType: "vehicle_type",
    question: "Welches Fahrzeug wird geprüft?",
    introText: "Vier kurze Fragen noch, damit wir optimal auf deinen Termin vorbereitet sind :)",
    options: [
      { label: "PKW (Auto)", value: "pkw" },
      { label: "Anhänger", value: "anhaenger" },
      { label: "PKW + Anhänger", value: "pkw_anhaenger" },
      { label: "Motorrad/Moped", value: "motorrad_moped" },
      { label: "LKW", value: "lkw" },
      { label: "Anderes", value: "anderes" },
    ],
  },
  {
    surveyType: "vehicle_age",
    question: "Wie alt ist dein Fahrzeug?",
    options: [
      { label: "Neu bis 5 Jahre", value: "0_5" },
      { label: "5-10 Jahre", value: "5_10" },
      { label: "10-15 Jahre", value: "10_15" },
      { label: "15-20 Jahre", value: "15_20" },
      { label: "Älter als 20 Jahre", value: "20_plus" },
    ],
  },
  {
    // Asks for the CURRENT Plakette expiry, not the cycle after this
    // appointment - hence "läuft ab" anchored to the sticker on the plate,
    // which the customer can physically check, and hence "Überfällig" being a
    // valid answer. Feeds HU upsell timing for non-HU Prüfarten. ADR-0048.
    //
    // A "pkw_anhaenger" booking covers two vehicles with two Plaketten and two
    // dates; this records one bucket, defined as the EARLIEST of them - the
    // date that decides when they next need an appointment. Deliberately NOT a
    // second conditional question: the vehicle type is known here, so it would
    // be easy, but it taxes every combo customer for an error we self-correct
    // by calling a few weeks late. ADR-0048 records the rejection.
    surveyType: "next_inspection_date",
    question: "Wann läuft deine HU-Plakette ab?",
    options: [
      { label: "Überfällig", value: "overdue" },
      { label: "Diesen Monat", value: "this_month" },
      { label: "Nächsten Monat", value: "next_month" },
      { label: "In 2–3 Monaten", value: "2_3_months" },
      { label: "Später als 3 Monate", value: "later" },
      { label: "Weiß nicht", value: "unknown" },
    ],
  },
  {
    surveyType: "referral",
    question: "Woher kennst du uns?",
    options: [
      { label: "Freunde & Familie", value: "friends_family" },
      { label: "Google Suche", value: "google_search" },
      { label: "Social Media (Instagram, TikTok\u2026)", value: "social_media" },
      { label: "Flyer / Zeitung", value: "flyer_newspaper" },
      { label: "Empfehlung einer Werkstatt", value: "workshop_referral" },
      { label: "Ich war schon mal Kunde", value: "returning_customer" },
      { label: "Sonstiges", value: "other" },
    ],
  },
]

// ─── Progress Bar ──────────────────────────────────────────────

const ProgressBar: FC<{ step: number; total: number }> = ({ step, total }) => (
  <View style={$progressContainer}>
    {Array.from({ length: total }).map((_, i) => (
      <View key={i} style={[$progressSegment, i <= step && $progressSegmentActive]} />
    ))}
  </View>
)

// ─── Survey Button with hover ──────────────────────────────────
const SurveyButton: FC<{
  label: string
  onPress: () => void
}> = ({ label, onPress }) => {
  const [hovered, setHovered] = useState(false)

  const webHoverProps =
    Platform.OS === "web"
      ? {
          onMouseEnter: () => setHovered(true),
          onMouseLeave: () => setHovered(false),
        }
      : {}

  return (
    <TouchableOpacity
      style={[$button, hovered && $buttonHovered]}
      onPress={onPress}
      activeOpacity={0.7}
      {...webHoverProps}
    >
      <Text style={[$buttonText, hovered && $buttonTextHovered]}>{label}</Text>
    </TouchableOpacity>
  )
}

// ─── Main Component ────────────────────────────────────────────
export const ReferralSurvey: FC<ReferralSurveyProps> = ({
  trackingToken,
  visible,
  onSurveyShow,
  onSurveyHide,
}) => {
  const [phase, setPhase] = useState<Phase>("idle")
  const [currentStep, setCurrentStep] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const thankYouTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const hasBeenVisibleRef = useRef(false)
  const onSurveyShowRef = useRef(onSurveyShow)
  onSurveyShowRef.current = onSurveyShow
  const [windowWidth, setWindowWidth] = useState(Dimensions.get("window").width)
  const [windowHeight, setWindowHeight] = useState(Dimensions.get("window").height)
  const isMobile = windowWidth < 768

  useEffect(() => {
    if (Platform.OS !== "web") return
    const handleResize = () => {
      setWindowWidth(Dimensions.get("window").width)
      setWindowHeight(Dimensions.get("window").height)
    }
    const subscription = Dimensions.addEventListener("change", handleResize)
    return () => subscription?.remove()
  }, [])

  // ─── Visibility & Timer Logic ─────────────────────────────────
  useEffect(() => {
    if (!trackingToken) {
      setPhase("hidden")
      return
    }

    if (visible && phase === "idle" && !hasBeenVisibleRef.current) {
      hasBeenVisibleRef.current = true
      idleTimerRef.current = setTimeout(() => {
        setPhase("asking")
        onSurveyShowRef.current?.()
      }, 500)
    }

    if (!visible && phase === "idle" && idleTimerRef.current) {
      clearTimeout(idleTimerRef.current)
      idleTimerRef.current = null
    }

    // Don't auto-hide during asking or thankyou — let the user finish and the timer run

    return () => {
      if (idleTimerRef.current) {
        clearTimeout(idleTimerRef.current)
        idleTimerRef.current = null
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible, phase, trackingToken])

  // ─── Dismiss (X button) ─────────────────────────────────────
  const handleDismiss = useCallback(() => {
    setPhase("hidden")
    onSurveyHide?.()
  }, [onSurveyHide])

  // ─── Answer a question ──────────────────────────────────────
  const handleAnswer = useCallback(
    (answer: string) => {
      if (isSubmitting) return
      setIsSubmitting(true)

      const step = SURVEY_STEPS[currentStep]

      // Fire-and-forget: submit this step's answer
      submitReferralSurvey({
        trackingToken,
        answer,
        surveyType: step.surveyType,
      }).catch((error) => {
        Sentry.captureException(error, {
          tags: { flow: "survey", operation: `submit-${step.surveyType}` },
        })
      })

      if (currentStep < SURVEY_STEPS.length - 1) {
        // Advance to next question, re-enable after brief delay for animation
        setCurrentStep((prev) => prev + 1)
        setTimeout(() => setIsSubmitting(false), 300)
      } else {
        // Last question — show thank you
        setPhase("thankyou")

        thankYouTimerRef.current = setTimeout(() => {
          setPhase("hidden")
          onSurveyHide?.()
        }, 3000)
      }
    },
    [trackingToken, currentStep, isSubmitting, onSurveyHide],
  )

  useEffect(() => {
    return () => {
      if (thankYouTimerRef.current) clearTimeout(thankYouTimerRef.current)
    }
  }, [])

  // ─── Render nothing if hidden or idle ─────────────────────────
  if (phase === "hidden" || phase === "idle") return null

  const currentStepData = SURVEY_STEPS[currentStep]

  // ─── Asking Phase ─────────────────────────────────────────────
  if (phase === "asking") {
    return (
      <Animated.View
        entering={SlideInDown.springify().damping(20).stiffness(120)}
        exiting={FadeOut.duration(200)}
        // The card is absolutely anchored above the bottom sheet (bottom: 32%),
        // so on short viewports it must cap its height and let the answer list
        // scroll — otherwise the progress row and close button clip off-screen.
        style={[$card, isMobile && $cardMobile, { maxHeight: windowHeight * 0.6 }]}
      >
        {/* Progress Bar + Close button on same row */}
        <Animated.View entering={FadeIn.delay(100)} style={$progressRow}>
          <View style={$progressBarWrapper}>
            <ProgressBar step={currentStep} total={SURVEY_STEPS.length} />
          </View>
          <TouchableOpacity style={$closeButton} onPress={handleDismiss} activeOpacity={0.6}>
            <Icon name="close" size={16} color={colors.textDim} />
          </TouchableOpacity>
        </Animated.View>

        {/* Intro Text — only on first question, hidden on mobile */}
        {currentStep === 0 && !isMobile && currentStepData.introText && (
          <Animated.View entering={FadeIn.delay(200)}>
            <Text style={$introText}>{currentStepData.introText}</Text>
          </Animated.View>
        )}

        {/* Question */}
        <View>
          <Text style={[$questionText, isMobile && $questionTextMobile]}>
            {currentStepData.question}
          </Text>
        </View>

        {/* Answer Buttons — scrollable so a capped card keeps every option reachable */}
        <ScrollView
          style={$buttonsScroll}
          contentContainerStyle={[$buttonsContainer, isMobile && $buttonsContainerMobile]}
          showsVerticalScrollIndicator={false}
        >
          {currentStepData.options.map((option) => (
            <View key={option.value} style={$buttonWrapper}>
              <SurveyButton label={option.label} onPress={() => handleAnswer(option.value)} />
            </View>
          ))}
        </ScrollView>
      </Animated.View>
    )
  }

  // ─── Thank You Phase ──────────────────────────────────────────
  if (phase === "thankyou") {
    return (
      <Animated.View
        entering={FadeIn.duration(400)}
        exiting={FadeOut.duration(300)}
        style={[$card, $thankYouCard, isMobile && $cardMobile]}
      >
        <CelebrationBurst />

        <Animated.View entering={FadeIn.delay(100)}>
          <Text style={$checkmark}>🎉</Text>
          <Text style={[$thankYouTitle, isMobile && $thankYouTitleMobile]}>Danke!</Text>
          <Text style={[$thankYouSubtitle, isMobile && $thankYouSubtitleMobile]}>
            Wir bereiten jetzt die finalen Schritte vor.
          </Text>
        </Animated.View>
      </Animated.View>
    )
  }

  return null
}

// ─── Styles ─────────────────────────────────────────────────────

const $card: ViewStyle = {
  backgroundColor: colors.surface,
  borderRadius: 20,
  padding: spacing.lg,
  paddingTop: spacing.lg,
  alignItems: "center",
  width: 360,
  position: "relative",
  shadowColor: colors.palette.neutral900,
  shadowOffset: { width: 0, height: 4 },
  shadowOpacity: 0.12,
  shadowRadius: 16,
  elevation: 8,
  ...Platform.select({
    web: {
      boxShadow: "0 4px 24px rgba(0, 0, 0, 0.12)",
    } as any,
  }),
}

const $cardMobile: ViewStyle = {
  width: 300,
  padding: spacing.sm,
  paddingTop: spacing.md,
  marginTop: spacing.xs,
}

const $progressRow: ViewStyle = {
  flexDirection: "row",
  alignItems: "center",
  width: "100%",
  marginBottom: spacing.md,
  gap: spacing.sm,
}

const $closeButton: ViewStyle = {
  width: 24,
  height: 24,
  borderRadius: 12,
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  ...Platform.select({
    web: { cursor: "pointer" } as any,
  }),
}

const $progressBarWrapper: ViewStyle = {
  flex: 1,
}

const $progressContainer: ViewStyle = {
  flexDirection: "row",
  gap: 4,
  width: "100%",
}

const $progressSegment: ViewStyle = {
  flex: 1,
  height: 4,
  borderRadius: 2,
  backgroundColor: colors.palette.neutral200,
}

const $progressSegmentActive: ViewStyle = {
  backgroundColor: colors.primary,
}

const $introText: TextStyle = {
  fontSize: 14,
  fontFamily: typography.primary.normal,
  color: colors.textDim,
  textAlign: "center",
  marginBottom: spacing.md,
  lineHeight: 20,
}

const $questionText: TextStyle = {
  fontSize: 18,
  fontFamily: typography.primary.bold,
  color: colors.text,
  textAlign: "center",
  marginBottom: spacing.md,
}

const $questionTextMobile: TextStyle = {
  fontSize: 16,
}

// flexShrink lets the answer list absorb the card's maxHeight cap (header rows
// keep their intrinsic size) so overflow becomes scrolling instead of clipping.
const $buttonsScroll: ViewStyle = {
  width: "100%",
  flexShrink: 1,
}

const $buttonsContainer: ViewStyle = {
  width: "100%",
  gap: spacing.xs,
}

const $buttonsContainerMobile: ViewStyle = {
  gap: spacing.xxs,
}

const $buttonWrapper: ViewStyle = {
  width: "100%",
}

const $button: ViewStyle = {
  backgroundColor: colors.surface,
  borderRadius: 50,
  borderWidth: 1.5,
  borderColor: colors.palette.neutral200,
  paddingVertical: 11,
  paddingHorizontal: spacing.lg,
  alignItems: "center",
  justifyContent: "center",
  ...Platform.select({
    web: {
      cursor: "pointer",
      transition: "all 0.15s ease",
    } as any,
  }),
}

const $buttonHovered: ViewStyle = {
  borderColor: colors.primary,
  backgroundColor: "rgba(255, 56, 47, 0.05)",
}

const $buttonText: TextStyle = {
  fontSize: 14,
  fontFamily: typography.primary.medium,
  color: colors.text,
  textAlign: "center",
}

const $buttonTextHovered: TextStyle = {
  color: colors.primary,
}

const $thankYouCard: ViewStyle = {
  paddingVertical: spacing.xxl,
}

const $checkmark: TextStyle = {
  fontSize: 40,
  textAlign: "center",
  marginBottom: spacing.sm,
}

const $thankYouTitle: TextStyle = {
  fontSize: 22,
  fontFamily: typography.primary.bold,
  color: colors.text,
  textAlign: "center",
  marginBottom: spacing.xxs,
}

const $thankYouTitleMobile: TextStyle = {
  fontSize: 19,
}

const $thankYouSubtitle: TextStyle = {
  fontSize: 14,
  fontFamily: typography.primary.normal,
  color: colors.textDim,
  textAlign: "center",
}

const $thankYouSubtitleMobile: TextStyle = {
  fontSize: 13,
}
